import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import {getThemeName} from "./themeUtils";

export const isQuestionShown = passenger => (question) => {
    if(question.disabled) {
        return false;
    }

    if(question.filters) {
        // loop over filters and check each one, returning false if one fails
        return question.filters.every(filter => passengerMatchesFilter(filter, passenger))
    }

    return true;
}

export const getNrChildQuestionsForPart = (question, answer, part) => {
    const childQuestions = question.questions &&
        question.questions[answer] &&
        question.questions[answer].filter(childQuestion => childQuestion.part === part).length || 0;

    return childQuestions;
}

function passengerMatchesFilter(filter, passenger) {
    switch (filter.operator) {
        case "eq":
            return passenger[filter.key] === filter.value;

        // TODO - add type validation if needed
        case "lt":
            return passenger[filter.key] < parseInt(filter.value);

        case "gt":
            return passenger[filter.key] > parseInt(filter.value);

        default :
            return false;
    }
}

export function getAnswerErrorMessage(question, answer) {
    let errorMessage = "";
    const rules = question.validation || [];
    const regexpString = question.regularExpression;
    const regexpMessage = question.regularExpressionMessage || t({id: "answer_provided_is_invalid"});

    rules.every(rule => {
        const [operation, ...params] = rule.split("|");
        switch (operation) {
            case "empty":
                if(answer) {
                    errorMessage = t({id: "you_cannot_continue_with_this_field_selected"})
                    return false
                }
                return true;

            case "required":
                if(!answer || answer.length === 0) {
                    errorMessage = t({id: "this_field_is_required_please_fill_it_out"});
                    return false;
                }
                return true;

            case "min":
                if(answer && answer.length < params[0]) {
                    errorMessage = i18n._({
                        id: "this_field_has_minimum_length_of",
                        message: 'This field has a minimum length of {num}.',
                        values: {num: params[0]}
                    })
                    return false;
                }
                return true;

            case "max":
                if(answer && answer.length > params[0]) {
                    errorMessage = i18n._({
                        id: "this_field_has_maximum_length_of",
                        message: 'This field has a maximum length of {num}.',
                        values: {num: params[0]}
                    })
                    return false;
                }
                return true;

            case "phone":
                if(!/^\+?[0-9- ]{6,15}$/.test(answer)) {
                    errorMessage = t({id: "this_field_must_be_avalid_phone_number"});
                    return false;
                }
                return true;

            case "minValue":
                // only allow digits
                if(!/^[0-9]+$/.test(answer)) {
                    errorMessage = t({id: "this_field_can_only_contain_digits"});
                    return false;
                }
                // check the intvalue - if we need to support decimal numbers implement it with floats
                if(parseInt(answer) < parseInt(params[0])) {
                    errorMessage = t({id: "your_entry_below_the_allowed_value"});
                    return false;
                }
                return true;

            case "maxValue":
                // only allow digits
                if(!/^[0-9]+$/.test(answer)) {
                    errorMessage = t({id: "this_field_can_only_contain_digits"});
                    return false;
                }
                // check the intvalue - if we need to support decimal numbers implement it with floats
                if(parseInt(answer) > parseInt(params[0])) {
                    errorMessage = t({id: "your_entry_exceeded_the_allowed_value"});
                    return false;
                }

                return true;

            default:
                return true;
        }
    });

    // use the regexp to do the last round of server validation if errorMessage is still empty
    // TODO - implement this
    // if(!errorMessage && regexpString) {
    //     try {
    //         const regex = new RegExp(regexpString);
    //
    //         if(!regex.test(answer)) {
    //             errorMessage = regexpMessage;
    //         }
    //     }
    //     catch (e) {
    //         console.log("error with regexp", regexpString);
    //     }
    // }

    return errorMessage;
}

export const getQuestionErrors = (question, activePart, answers, passenger) => {
    const errors = {};

    const answer = answers[question.id];
    // validate answer to question itself, if it is currently shown
    const errorMessage = question.part === activePart ? getAnswerErrorMessage(question, answer) : false;
    if(errorMessage) {
        errors[question.id] = errorMessage;
    }

    // loop over nested visible fields and validate each one, building up the object of errors as we go
    let nestedErrors = {};
    const nestedQuestions = question.questions && question.questions[answer]?.filter(isQuestionShown(passenger));
    if(nestedQuestions) {
        nestedQuestions.forEach(nestedQuestion => {
            nestedErrors = {...nestedErrors, ...getQuestionErrors(nestedQuestion, activePart, answers, passenger)};
        });
    }

    return {...errors, ...nestedErrors};
}

export const getErrors = (questions, answers, passenger, activePart) => {
    let errors = {};
    questions.forEach(question => {
        errors = {...errors, ...getQuestionErrors(question, activePart, answers, passenger)};
    });

    return errors;
}

export const isFormValid = (questions, answers, passenger, activePart) => {
    if(questions.length === 0) {
        return false;
    }

    return questions.every(question =>
        Object.keys(getQuestionErrors(question, activePart, answers, passenger)).length === 0
    )
}

const aclSections = [
    {
        hubId: "emergencyContact",
        label: "Emergency Contact",
        reviewLabel: "Emergency Contact",
        preFormLabel: "Emergency Contact",
    },
    {
        hubId: "medical",
        label: "Travel Insurance",
        reviewLabel: "Travel Insurance",
        preFormLabel: "Travel Insurance",
    },
    {
        hubId: "allergy",
        label: "Allergies and diet",
        reviewLabel: "Allergies and diet",
        preFormLabel: "Allergy and Dietary needs",
    },
    {
        hubId: "vaccination",
        label: "Vaccinations",
        reviewLabel: "Vaccinations",
        preFormLabel: "COVID vaccination",
    },
    {
        hubId: "medicalMobility",
        label: "Medical and Mobility",
        reviewLabel: "Medical and Mobility",
        preFormLabel: "Medical and Mobility",
    },
    // TODO - uncomment this once card registration function should be in place again for AMB
    // {
    //     hubId: "cardDetails",
    //     label: "Add a card",
    //     reviewLabel: "Card details",
    // },
];

const cfcSections = [
    {
        hubId: "emergencyContact",
        label: "Contact en cas d'urgence",
        reviewLabel: "Contact en cas d'urgence",
        preFormLabel: "Contact en cas d'urgence",
    },
    {
        hubId: "medical",
        label: "Assurance voyage",
        reviewLabel: "Assurance voyage",
        preFormLabel: "Assurance voyage",
    },
    {
        hubId: "allergy",
        label: "Allergies et régimes alimentaires",
        reviewLabel: "Allergies et régimes alimentaires",
        preFormLabel: "Allergies et régimes alimentaires",
    },
    {
        hubId: "vaccination",
        label: "Vaccinations",
        reviewLabel: "Vaccinations",
        preFormLabel: "COVID vaccination",
    },
    {
        hubId: "medicalMobility",
        label: "Medical and Mobility",
        reviewLabel: "Medical and Mobility",
        preFormLabel: "Medical and Mobility",
    }
];

export const sections = getSections();


function getSections() {
    const themeName = getThemeName();
    switch (themeName) {
        case "acl":
            return aclSections;
        case "cfc":
            return cfcSections;
        default:
            return aclSections;
    }
}